import React, { useState, useEffect } from "react";
import { Icon } from "@iconify/react";
import Breadcrumb from "../components/Breadcrumb";
import { handleCustomResponse } from "../utils/response";
import { staffApi } from "../Apis/Api";
import LoadingComponent from "../components/ui/LoadingComponent";
import ErrorAlert from "../components/ui/ErrorAlert";

const MyIncentiveWallet = () => {
  const [walletData, setWalletData] = useState({
    earnedBalance: 0,
    redeemedBalance: 0,
    earnedPoints: 0,
    redeemedPoints: 0,
    currencies: [],
  });
  const [transactions, setTransactions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [activeTab, setActiveTab] = useState("transactions");
  const [transactionPagination, setTransactionPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    hasMore: false,
  });
  const [error, setError] = useState();

  // Get user currency from localStorage
  const getDefaultCurrency = () => {
    try {
      const userData = localStorage.getItem("user");
      if (userData) {
        const user = JSON.parse(userData);
        return user.currency_code || "AED"; // Default to AED if not found
      }
    } catch (error) {
      console.error("Error getting user currency from localStorage:", error);
    }
    return "AED"; // Fallback currency
  };

  const defaultCurrency = getDefaultCurrency();

  // Redeem popup state
  const [showRedeemPopup, setShowRedeemPopup] = useState(false);
  const [redeemType, setRedeemType] = useState("points"); // "points" or "currency"
  const [redeemAmount, setRedeemAmount] = useState("");
  const [selectedCurrency, setSelectedCurrency] = useState(defaultCurrency); // Set default currency here
  const [isSubmittingRedeem, setIsSubmittingRedeem] = useState(false);

  // Get staff ID from localStorage, user data, or auth context
  const getStaffId = () => {
    // Method 1: From localStorage
    const staffId =
      localStorage.getItem("staffId") || localStorage.getItem("user_id");

    // Method 2: From user data in localStorage
    const userData = localStorage.getItem("user");
    if (userData) {
      try {
        const parsedUser = JSON.parse(userData);
        return parsedUser.id || parsedUser.staff_id || staffId;
      } catch (error) {
        console.error("Error parsing user data:", error);
      }
    }

    // Method 3: From auth token payload (if you store user info in token)
    const token =
      localStorage.getItem("token") || localStorage.getItem("authToken");
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split(".")[1]));
        return payload.sub || payload.user_id || payload.staff_id;
      } catch (error) {
        console.error("Error parsing token:", error);
      }
    }

    // Fallback - should not happen in production
    console.warn("Staff ID not found, using fallback");
    return staffId || null;
  };

  useEffect(() => {
    fetchWalletData();
    fetchTransactions(1, true); // Load first page of transactions
  }, []);

  // Set default currency when popup opens
  useEffect(() => {
    if (showRedeemPopup) {
      setSelectedCurrency(defaultCurrency);
    }
  }, [showRedeemPopup, defaultCurrency]);

  const fetchWalletData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const staffId = getStaffId();

      // Check if staff ID exists
      if (!staffId) {
        handleCustomResponse(
          "error",
          "Staff ID not found. Please login again."
        );
        return;
      }

      console.log("Fetching wallet data for staff ID:", staffId);

      // Call the actual API
      const response = await staffApi.getStaffWallet(staffId);

      if (response.success) {
        // Calculate total earned and redeemed amounts from all currencies
        let totalEarned = 0;
        let totalRedeemed = 0;

        if (response.data.currencies && response.data.currencies.length > 0) {
          response.data.currencies.forEach((currency) => {
            totalEarned += parseFloat(currency.earned_amount) || 0;
            totalRedeemed += parseFloat(currency.redeemed_currency) || 0;
          });
        }

        setWalletData({
          earnedBalance: totalEarned,
          redeemedBalance: totalRedeemed,
          earnedPoints: response.data.points_earned || 0,
          redeemedPoints: response.data.points_redeemed || 0,
          currencies: response.data.currencies || [],
        });
      } else {
        // handleCustomResponse("error", response.message || "Failed to load wallet data");
      }
    } catch (error) {
      console.error("Error fetching wallet data:", error);
      setError(true);
      // handleCustomResponse("error", "Failed to load wallet data");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchTransactions = async (page = 1, resetData = false) => {
    try {
      setError(null);
      if (page === 1) {
        setIsLoading(true);
      } else {
        setIsLoadingMore(true);
      }

      const response = await staffApi.getWalletTransactions({
        per_page: 20,
        page: page,
        sort_by: "created_datetime",
        sort_order: "desc",
      });

      if (response.success) {
        const newTransactions = response.data || [];

        // Transform API data to match your component's expected format
        const formattedTransactions = newTransactions.map((transaction) => {
          // Determine transaction type and description
          let type, description, status;

          switch (transaction.transaction_type) {
            case "EARN":
              if (parseFloat(transaction.currency) > 0) {
                type = "earned_currency";
                description = `Earned from ${
                  transaction.plan_name || "Incentive Plan"
                }`;
              } else if (parseFloat(transaction.points) > 0) {
                type = "earned_points";
                description = `Points earned from ${
                  transaction.plan_name || "Incentive Plan"
                }`;
              }
              status = "completed";
              break;
            case "REDEEM_REQUEST":
              if (parseFloat(transaction.currency) > 0) {
                type = "redeem_request_currency";
                description = `Currency redeem request (${
                  transaction.currency_code || "USD"
                } ${transaction.currency})`;
              } else if (parseFloat(transaction.points) > 0) {
                type = "redeem_request";
                description = `Points redeem request (${transaction.points} pts)`;
              }
              status =
                transaction.meta_json?.status?.toLowerCase() || "pending";
              break;
            case "REDEEM_APPROVE":
              if (parseFloat(transaction.currency) > 0) {
                type = "redeem_approved_currency";
                description = `Currency redeem approved (${
                  transaction.currency_code || "USD"
                } ${transaction.currency})`;
              } else if (parseFloat(transaction.points) > 0) {
                type = "redeem_approved";
                description = `Points redeem approved (${transaction.points} pts)`;
              }
              status = "approved";
              break;
            case "REDEEM_REJECT":
              if (parseFloat(transaction.currency) > 0) {
                type = "redeem_rejected_currency";
                description = `Currency redeem rejected (${
                  transaction.currency_code || "USD"
                } ${transaction.currency})`;
              } else if (parseFloat(transaction.points) > 0) {
                type = "redeem_rejected";
                description = `Points redeem rejected (${transaction.points} pts)`;
              }
              status = "rejected";
              break;
            default:
              type = "other";
              description = transaction.transaction_type;
              status = "completed";
          }

          return {
            id: transaction.transaction_id,
            type: type,
            amount: parseFloat(transaction.currency) || 0,
            currency: transaction.currency_code || "USD",
            points: parseFloat(transaction.points) || 0,
            description: description,
            date: transaction.created_datetime,
            status: status,
            plan_name: transaction.plan_name,
          };
        });

        if (resetData || page === 1) {
          setTransactions(formattedTransactions);
        } else {
          setTransactions((prev) => [...prev, ...formattedTransactions]);
        }

        // Update pagination info
        setTransactionPagination({
          currentPage: response.pagination?.current_page || page,
          totalPages: response.pagination?.last_page || 1,
          hasMore:
            (response.pagination?.current_page || page) <
            (response.pagination?.last_page || 1),
        });
      } else {
        // handleCustomResponse("error", response.message || "Failed to load transactions");
      }
    } catch (error) {
      setError(true);
      console.error("Error fetching transactions:", error);
      // handleCustomResponse("error", "Failed to load transactions");
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  };

  const loadMoreTransactions = () => {
    if (!isLoadingMore && transactionPagination.hasMore) {
      fetchTransactions(transactionPagination.currentPage + 1, false);
    }
  };

  // Handle redeem request submission
  const handleRedeemRequest = async () => {
    try {
      setError(null);
      if (!redeemAmount || parseFloat(redeemAmount) <= 0) {
        handleCustomResponse("error", "Please enter a valid amount");
        return;
      }

      setIsSubmittingRedeem(true);

      let requestData = {};

      if (redeemType === "points") {
        // Check if user has enough points
        if (parseFloat(redeemAmount) > walletData.earnedPoints) {
          handleCustomResponse("error", "Insufficient points balance");
          return;
        }

        requestData = {
          points_requested: parseFloat(redeemAmount),
          currency_code: "",
        };
      } else {
        // Currency redeem
        if (parseFloat(redeemAmount) > walletData.earnedBalance) {
          handleCustomResponse("error", "Insufficient currency balance");
          return;
        }

        requestData = {
          currency_equivalent: parseFloat(redeemAmount),
          currency_code: selectedCurrency,
        };
      }

      // Add the API call to staffApi
      const response = await staffApi.submitRedeemRequest(requestData);

      if (response.success) {
        handleCustomResponse(
          "success",
          "Redeem request submitted successfully!"
        );
        setShowRedeemPopup(false);
        setRedeemAmount("");

        // Refresh wallet data and transactions
        fetchWalletData();
        fetchTransactions(1, true);
      } else {
        // handleCustomResponse("error", response.message || "Failed to submit redeem request");
      }
    } catch (error) {
      console.error("Error submitting redeem request:", error);
      setError(true);
      // handleCustomResponse("error", "Failed to submit redeem request");
    } finally {
      setIsSubmittingRedeem(false);
    }
  };

  const getTransactionIcon = (type) => {
    switch (type) {
      case "earned_currency":
        return "heroicons:banknotes";
      case "earned_points":
        return "heroicons:star";
      case "redeem_request":
      case "redeem_request_currency":
        return "heroicons:arrow-up-right";
      case "redeem_approved":
      case "redeem_approved_currency":
        return "heroicons:check-circle";
      case "redeem_rejected":
      case "redeem_rejected_currency":
        return "heroicons:x-circle";
      default:
        return "heroicons:arrow-down-left";
    }
  };

  const getTransactionColor = (type) => {
    switch (type) {
      case "earned_currency":
        return "text-success-600";
      case "earned_points":
        return "text-warning-600";
      case "redeem_request":
      case "redeem_request_currency":
        return "text-info-600";
      case "redeem_approved":
      case "redeem_approved_currency":
        return "text-success-600";
      case "redeem_rejected":
      case "redeem_rejected_currency":
        return "text-danger-600";
      default:
        return "text-neutral-600";
    }
  };

  const getTransactionTypeLabel = (type) => {
    switch (type) {
      case "earned_currency":
        return "Currency";
      case "earned_points":
        return "Points";
      case "redeem_request":
      case "redeem_request_currency":
        return "Redeem";
      case "redeem_approved":
      case "redeem_approved_currency":
        return "Redeemed";
      case "redeem_rejected":
      case "redeem_rejected_currency":
        return "Rejected";
      default:
        return "Transaction";
    }
  };

  const formatCurrency = (amount, currency = "USD") => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
    }).format(Math.abs(amount));
  };

  const formatPoints = (points) => {
    return `${points.toLocaleString()} pts`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getTransactionAmount = (transaction) => {
    if (transaction.type.includes("earned")) {
      // For earned transactions, show positive values
      if (transaction.amount > 0) {
        return `${formatCurrency(transaction.amount, transaction.currency)}`;
      } else if (transaction.points > 0) {
        return `${formatPoints(transaction.points)}`;
      }
    } else if (transaction.type.includes("redeem")) {
      // For redeem transactions, show negative values
      if (transaction.amount > 0) {
        return `${formatCurrency(transaction.amount, transaction.currency)}`;
      } else if (transaction.points > 0) {
        return `${formatPoints(transaction.points)}`;
      }
    }
    return "-";
  };

  // Get primary currency (first one or default currency)
  const primaryCurrency =
    walletData.currencies && walletData.currencies.length > 0
      ? walletData.currencies[0].currency_code
      : defaultCurrency;

  if (isLoading) {
    return (
      <>
        <Breadcrumb title="My Incentive Wallet" />
        <LoadingComponent />
      </>
    );
  }

  if (error) {
    return <ErrorAlert />;
  }
  return (
    <>
      <Breadcrumb title="My Incentive Wallet" />

      {/* Wallet Overview Cards */}
      <div className="row g-20 mb-24">
        <div className="col-xl-3 col-sm-6">
          <div className="card shadow-none border bg-gradient-start-1 h-100">
            <div className="card-body p-20">
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                <div>
                  <p className="fw-medium text-primary-light mb-1">
                    Earned Balance
                  </p>
                  <h6 className="mb-0">
                    {formatCurrency(walletData.earnedBalance, primaryCurrency)}
                  </h6>
                </div>
                <div className="w-50-px h-50-px bg-cyan rounded-circle d-flex justify-content-center align-items-center">
                  <Icon
                    icon="solar:wallet-bold-duotone"
                    className="text-white text-2xl mb-0"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-xl-3 col-sm-6">
          <div className="card shadow-none border bg-gradient-start-2 h-100">
            <div className="card-body p-20">
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                <div>
                  <p className="fw-medium text-primary-light mb-1">
                    Earned Points
                  </p>
                  <h6 className="mb-0">
                    {formatPoints(walletData.earnedPoints)}
                  </h6>
                </div>
                <div className="w-50-px h-50-px bg-warning-600 rounded-circle d-flex justify-content-center align-items-center">
                  <Icon
                    icon="solar:star-bold-duotone"
                    className="text-white text-2xl mb-0"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-xl-3 col-sm-6">
          <div className="card shadow-none border bg-gradient-start-3 h-100">
            <div className="card-body p-20">
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                <div>
                  <p className="fw-medium text-primary-light mb-1">
                    Redeemed Balance
                  </p>
                  <h6 className="mb-0">
                    {formatCurrency(
                      walletData.redeemedBalance,
                      primaryCurrency
                    )}
                  </h6>
                </div>
                <div className="w-50-px h-50-px bg-success-600 rounded-circle d-flex justify-content-center align-items-center">
                  <Icon
                    icon="solar:chart-square-bold-duotone"
                    className="text-white text-2xl mb-0"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-xl-3 col-sm-6">
          <div className="card shadow-none border bg-gradient-start-4 h-100">
            <div className="card-body p-20">
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                <div>
                  <p className="fw-medium text-primary-light mb-1">
                    Redeemed Points
                  </p>
                  <h6 className="mb-0">
                    {formatPoints(walletData.redeemedPoints)}
                  </h6>
                </div>
                <div className="w-50-px h-50-px bg-info rounded-circle d-flex justify-content-center align-items-center">
                  <Icon
                    icon="solar:medal-star-bold-duotone"
                    className="text-white text-2xl mb-0"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Card - Only Transactions */}
      <div className="card h-100 p-0 radius-12">
        <div className="card-body p-24">
          {/* Header and Redeem Button */}
          <div className="d-flex align-items-center justify-content-between mb-24">
            <div className="d-flex align-items-center gap-2">
              <Icon
                icon="solar:history-bold"
                className="text-primary-600"
                style={{ fontSize: "20px" }}
              />
              <h5 className="mb-0">Transaction History</h5>
            </div>
            <button
              className="btn btn-primary-600 d-flex align-items-center gap-2 px-20 py-11"
              onClick={() => setShowRedeemPopup(true)}
            >
              <Icon icon="heroicons:arrow-up-right" className="text-lg" />
              Redeem Now
            </button>
          </div>

          {/* Loading State for Initial Load */}
          {isLoading && transactions.length === 0 ? (
            <LoadingComponent />
          ) : (
            <>
              {/* Transactions Table */}
              <div className="table-responsive">
                <table className="table bordered-table mb-0">
                  <thead>
                    <tr>
                      <th scope="col" className="text-nowrap">
                        Type
                      </th>
                      <th scope="col" className="d-none d-md-table-cell">
                        Description
                      </th>
                      <th scope="col" className="text-nowrap">
                        Amount
                      </th>
                      <th scope="col" className="d-none d-sm-table-cell">
                        Date
                      </th>
                      <th scope="col" className="text-nowrap">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.length > 0 ? (
                      transactions.map((transaction) => (
                        <tr key={transaction.id}>
                          <td className="align-middle">
                            <div className="d-flex align-items-center gap-1 gap-md-2 flex-nowrap">
                              <Icon
                                icon={getTransactionIcon(transaction.type)}
                                className={`flex-shrink-0 ${getTransactionColor(
                                  transaction.type
                                )}`}
                                style={{ fontSize: "18px" }}
                              />
                              <span className="fw-medium text-capitalize d-none d-lg-inline">
                                {getTransactionTypeLabel(transaction.type)}
                              </span>
                            </div>
                            {/* Mobile Description */}
                            <div className="d-md-none mt-1">
                              <small className="text-secondary-light">
                                {transaction.description}
                              </small>
                            </div>
                          </td>
                          <td className="align-middle d-none d-md-table-cell">
                            <span className="text-secondary-light">
                              {transaction.description}
                            </span>
                          </td>
                          <td className="align-middle">
                            <span
                              className={`fw-semibold ${getTransactionColor(
                                transaction.type
                              )} text-nowrap`}
                            >
                              {getTransactionAmount(transaction)}
                            </span>
                            {/* Mobile Date */}
                            <div className="d-sm-none mt-1">
                              <small className="text-secondary-light">
                                {formatDate(transaction.date)}
                              </small>
                            </div>
                          </td>
                          <td className="align-middle d-none d-sm-table-cell">
                            <span className="text-secondary-light text-nowrap">
                              {formatDate(transaction.date)}
                            </span>
                          </td>
                          <td className="align-middle">
                            <span
                              className={`badge text-sm fw-semibold text-nowrap ${
                                transaction.status === "completed"
                                  ? "text-success-600 bg-success-100"
                                  : transaction.status === "pending"
                                  ? "text-warning-600 bg-warning-100"
                                  : transaction.status === "approved"
                                  ? "text-success-600 bg-success-100"
                                  : transaction.status === "rejected"
                                  ? "text-danger-600 bg-danger-100"
                                  : "text-secondary-light bg-secondary-100"
                              } px-12 px-md-20 py-6 py-md-9 radius-4`}
                            >
                              {transaction.status.charAt(0).toUpperCase() +
                                transaction.status.slice(1)}
                            </span>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="5" className="text-center py-4">
                          <Icon
                            icon="solar:wallet-bold-duotone"
                            className="text-6xl text-neutral-400 mb-3"
                          />
                          <p className="text-secondary-light mb-0">
                            No transactions found
                          </p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Load More Button */}
              {transactionPagination.hasMore && (
                <div className="text-center mt-4">
                  <button
                    className="btn btn-outline-primary px-4 py-2"
                    onClick={loadMoreTransactions}
                    disabled={isLoadingMore}
                  >
                    {isLoadingMore ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                        >
                          <span className="visually-hidden">Loading...</span>
                        </span>
                        Loading More...
                      </>
                    ) : (
                      <>
                        <Icon icon="heroicons:arrow-down" className="me-2" />
                        Load More Transactions
                      </>
                    )}
                  </button>
                </div>
              )}

              {/* Pagination Info */}
              {transactions.length > 0 && (
                <div className="text-center mt-3">
                  <small className="text-secondary-light">
                    {transactionPagination.hasMore &&
                      ` • ${transactions.length} transactions loaded`}
                  </small>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Redeem Popup Modal */}
      {showRedeemPopup && (
        <div
          className="modal show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Redeem Request</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowRedeemPopup(false)}
                ></button>
              </div>
              <div className="modal-body">
                {/* Redeem Type Selection */}
                <div className="mb-3">
                  <label className="form-label">
                    What would you like to redeem?
                  </label>
                  <div className="row g-2">
                    <div className="col-6">
                      <button
                        type="button"
                        className={`btn w-100 d-flex flex-column align-items-center gap-2 py-3  ${
                          redeemType === "points"
                            ? "btn-primary-600"
                            : "btn-outline-600"
                        }`}
                        onClick={() => setRedeemType("points")}
                      >
                        <Icon
                          icon="heroicons:star"
                          className="text-2xl"
                          style={{ color: "var(--text-primary-light)" }}
                        />
                        <span style={{ color: "var(--text-primary-light)" }}>
                          Points
                        </span>
                        <small style={{ color: "var(--text-primary-light)" }}>
                          Available: {formatPoints(walletData.earnedPoints)}
                        </small>
                      </button>
                    </div>
                    <div className="col-6">
                      <button
                        type="button"
                        className={`btn w-100 d-flex flex-column align-items-center gap-2 py-3 ${
                          redeemType === "currency"
                            ? "btn-primary-600"
                            : "btn-outline-primary-600"
                        }`}
                        onClick={() => setRedeemType("currency")}
                      >
                        <Icon icon="heroicons:banknotes" className="text-2xl" />
                        <span>Currency</span>
                        <small>
                          Available:{" "}
                          {formatCurrency(
                            walletData.earnedBalance,
                            primaryCurrency
                          )}
                        </small>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Amount Input */}
                <div className="mb-3">
                  <label className="form-label">
                    {redeemType === "points"
                      ? "Points to Redeem"
                      : "Currency Amount to Redeem"}
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    value={redeemAmount}
                    onChange={(e) => setRedeemAmount(e.target.value)}
                    placeholder={
                      redeemType === "points" ? "Enter points" : "Enter amount"
                    }
                    min="1"
                    max={
                      redeemType === "points"
                        ? walletData.earnedPoints
                        : walletData.earnedBalance
                    }
                  />
                </div>

                {/* Currency Selection (only for currency redeem) - READ ONLY */}
                {redeemType === "currency" && (
                  <div className="mb-3">
                    <label className="form-label">Currency</label>
                    <input
                      type="text"
                      className="form-control"
                      value={selectedCurrency}
                      readOnly
                      disabled
                    />
                    <small style={{ color: "var(--text-primary-light)" }}>
                      Using your default currency: {selectedCurrency}
                    </small>
                  </div>
                )}

                {/* Summary */}
                {redeemAmount && (
                  <div className="alert alert-info">
                    <strong>Summary:</strong>
                    <br />
                    You are requesting to redeem{" "}
                    {redeemType === "points"
                      ? `${redeemAmount} points`
                      : `${selectedCurrency} ${redeemAmount}`}
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowRedeemPopup(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary-600"
                  onClick={handleRedeemRequest}
                  disabled={
                    isSubmittingRedeem ||
                    !redeemAmount ||
                    parseFloat(redeemAmount) <= 0
                  }
                >
                  {isSubmittingRedeem ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                      >
                        <span className="visually-hidden">Loading...</span>
                      </span>
                      Submitting...
                    </>
                  ) : (
                    "Submit Redeem Request"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MyIncentiveWallet;
